import { useSmoothReorder } from '../../hooks/useSmoothReorder.js'
import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { ChevronDown, ChevronUp, Eye, EyeOff, Settings, Trash2, MoreHorizontal, Plus, GripVertical } from 'lucide-react'
import { INDICATOR_DISPLAY_NAME } from './IndicatorPopover.jsx'
import { MULTI_INSTANCE_KINDS } from './indicatorRules.js'

// CE-04g-UI-chrome-2 — TradingView-style floating indicator/settings pill
// anchored to the top-left of the chart canvas. Replaces the row-style
// legacy indicator legend component. Collapsed = pill (active-count +
// chevron). Expanded = pill + body of indicator rows (eye / gear / trash /
// more) + a "+ Add Indicator" surface.
//
// Multi-instance gray-out rule lives ONLY in this dropdown (SMA/EMA stay
// enabled even with N already present; all other curated kinds gray out
// once one instance is on the chart). The ChartTopBar Indicators ▾ dropdown
// is unchanged — two add-surfaces coexist by design.

/** Curated indicator catalog for the + Add Indicator dropdown. */
export const AVAILABLE_INDICATORS = Object.freeze([
  { kind: 'sma', label: 'SMA' },
  { kind: 'ema', label: 'EMA' },
  { kind: 'keltner', label: 'Keltner Channel' },
  { kind: 'bollinger', label: 'Bollinger Bands' },
  { kind: 'donchian', label: 'Donchian Channels' },
  { kind: 'volume', label: 'Volume' },
  { kind: 'vwap_1d', label: 'VWAP 1D' },
  { kind: 'vwap_2d', label: 'VWAP 2D' },
  { kind: 'vwap_wtd', label: 'VWAP WTD' },
  { kind: 'vwap_mtd', label: 'VWAP MTD' },
  { kind: 'vwap_ytd', label: 'VWAP YTD' },
  { kind: 'vwap_earnings', label: 'VWAP Earnings' },
  { kind: 'vwap_earnings_2', label: 'VWAP 2 Earnings' },
  { kind: 'pivots', label: 'Pivot Points' },
  { kind: 'cars_v1', label: 'CA|RS' },
])

function indicatorTitle(ind) {
  const name = INDICATOR_DISPLAY_NAME[ind.kind] || ind.kind.toUpperCase()
  if (ind.kind === 'volume') return name
  if (ind.kind.startsWith('vwap_')) return name
  if (ind.kind === 'pivots') {
    const bs = ind.params?.bar_size ?? '1 day'
    return `${name} (${bs})`
  }
  if (ind.kind === 'cars_v1') {
    const benchmark = ind.params?.benchmark ?? 'SPY'
    return `${name} (${benchmark})`
  }
  const len = ind.params?.length ?? '?'
  if (ind.kind === 'sma' || ind.kind === 'ema') {
    const src = ind.params?.source ?? 'close'
    return `${name} ${len} ${src}`
  }
  if (ind.kind === 'keltner') {
    const mult = ind.params?.multiplier ?? '?'
    return `KC ${len} x ${mult}`
  }
  return `${name} ${len}`
}

function isInstanceVisible(ind) {
  const style = ind.style || {}
  if (typeof style.visible === 'boolean') return style.visible
  if (style.ma && typeof style.ma.visible === 'boolean') return style.ma.visible
  return true
}

export default function IndicatorAccordion({
  indicators,
  activeTimeframe,
  indicatorTemplates = [],
  onAdd,
  onAddTemplate,
  onToggleVisible,
  onOpenPopover,
  onRemove,
  onReorder,
  onSetAllVisible,
  onSaveTemplate,
}) {
  const [expanded, setExpanded] = useState(false)
  const [addMenuOpen, setAddMenuOpen] = useState(false)
  const [actionMenuPresetId, setActionMenuPresetId] = useState(null)
  const [draggingPresetId, setDraggingPresetId] = useState(null)
  const reorder = useSmoothReorder(indicators.map(ind => ind.presetId), (next, id) => {
    const index = next.indexOf(id)
    onReorder?.(id, index ? next[index - 1] : next[1], index ? 'after' : 'before')
  }, { disabled: !expanded, scope: activeTimeframe })
  const rootRef = useRef(null)
  const didMountRef = useRef(false)

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true
      return
    }
    setExpanded(false)
  }, [activeTimeframe])

  // Close open menus when clicking outside the accordion root.
  useEffect(() => {
    if (!addMenuOpen && !actionMenuPresetId) return
    function onMouseDown(e) {
      if (!rootRef.current || rootRef.current.contains(e.target)) return
      setAddMenuOpen(false)
      setActionMenuPresetId(null)
    }
    window.addEventListener('mousedown', onMouseDown)
    return () => window.removeEventListener('mousedown', onMouseDown)
  }, [addMenuOpen, actionMenuPresetId])

  const presentKinds = new Set(indicators.map((i) => i.kind))
  const hasVisibleIndicators = indicators.some(isInstanceVisible)
  const bulkToggleVisible = !hasVisibleIndicators
  const isDaily = activeTimeframe === 'daily' || activeTimeframe === '1D'
  const isOptDisabled = (kind) => {
    if (kind === 'cars_v1' && !isDaily) return true
    return !MULTI_INSTANCE_KINDS.has(kind) && presentKinds.has(kind)
  }
  const titleFor = (opt, disabled) => {
    if (opt.kind === 'cars_v1' && !isDaily) return 'CA|RS is daily-only'
    if (disabled) return `A ${opt.label} is already on the chart.`
    return `Add ${opt.label}`
  }

  const handleDragStart = (e, presetId) => {
    e.stopPropagation()
    setDraggingPresetId(presetId)
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move'
      e.dataTransfer.setData('text/plain', presetId)
    }
  }

  const handleDragOver = (e, targetPresetId) => {
    const draggedPresetId = draggingPresetId || e.dataTransfer?.getData?.('text/plain')
    if (!draggedPresetId || draggedPresetId === targetPresetId) return
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
  }

  const handleDrop = (e, targetPresetId) => {
    e.preventDefault()
    e.stopPropagation()
    const draggedPresetId = draggingPresetId || e.dataTransfer?.getData?.('text/plain')
    setDraggingPresetId(null)
    if (!draggedPresetId || draggedPresetId === targetPresetId) return

    const rect = e.currentTarget.getBoundingClientRect()
    const midpoint = rect.top + rect.height / 2
    const position = e.clientY > midpoint ? 'after' : 'before'
    onReorder?.(draggedPresetId, targetPresetId, position)
  }

  return (
    <div
      ref={rootRef}
      data-testid="indicator-accordion"
      style={{
        position: 'absolute',
        top: 8,
        left: 8,
        zIndex: 20,
      }}
      className="rounded-md border border-border bg-canvas/95 text-ink text-xs backdrop-blur-sm"
    >
      <button
        type="button"
        data-testid="accordion-pill"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-2 px-2 py-1 text-inkMuted hover:bg-canvas2 hover:text-ink rounded-md w-full text-left"
        aria-expanded={expanded}
      >
        <span>{indicators.length} indicators</span>
        {expanded ? <ChevronUp size={12} aria-hidden /> : <ChevronDown size={12} aria-hidden />}
      </button>

      <div
        data-testid="accordion-body"
        className={`border-t border-border px-1 py-1 min-w-[220px] ${
          expanded ? '' : 'hidden'
        }`}
      >
        {indicators.length === 0 && (
          <div className="px-2 py-1 text-inkMuted italic">No indicators</div>
        )}
        {indicators.length > 0 && typeof onSetAllVisible === 'function' && (
          <button
            type="button"
            data-testid="accordion-toggle-all-visibility"
            onClick={(e) => { e.stopPropagation(); onSetAllVisible(bulkToggleVisible) }}
            title={bulkToggleVisible ? 'Show all indicators' : 'Hide all indicators'}
            className="mb-1 flex w-full items-center gap-1.5 rounded px-1 py-1 text-left text-inkMuted hover:bg-canvas2 hover:text-ink"
            aria-label={bulkToggleVisible ? 'Show all indicators' : 'Hide all indicators'}
          >
            {bulkToggleVisible ? <Eye size={12} aria-hidden /> : <EyeOff size={12} aria-hidden />}
            <span className="flex-1">{bulkToggleVisible ? 'Show all indicators' : 'Hide all indicators'}</span>
          </button>
        )}
        {indicators.map((ind) => {
          const visible = isInstanceVisible(ind)
          return (
            <div
              key={ind.presetId}
              {...reorder.bind(ind.presetId)}
              onPointerDown={event => { if (event.metaKey && !event.target.closest('button')) reorder.start(event, ind.presetId) }}
              onClickCapture={event => reorder.consumeClick(event)}
              data-testid={`accordion-row-${ind.presetId}`}
              data-visible={String(visible)}
              data-dragging={String(draggingPresetId === ind.presetId)}
              style={{ ...reorder.bind(ind.presetId).style, ...(actionMenuPresetId === ind.presetId ? { zIndex: 40 } : {}) }}
              onDragOver={(e) => handleDragOver(e, ind.presetId)}
              onDrop={(e) => handleDrop(e, ind.presetId)}
              className={`transition-transform duration-200 ease-out motion-reduce:transition-none relative flex items-center gap-1.5 px-1 py-0.5 rounded hover:bg-canvas2 ${
                visible ? 'text-ink' : 'text-inkMuted opacity-60'
              } ${draggingPresetId === ind.presetId ? 'opacity-50' : ''}`}
            >
              <button
                type="button"
                data-testid={`accordion-drag-${ind.presetId}`}
                draggable={false}
                onPointerDown={event => reorder.start(event, ind.presetId)}
                onDragStart={(e) => handleDragStart(e, ind.presetId)}
                onDragEnd={() => setDraggingPresetId(null)}
                title="Drag to reorder visual stack"
                className="touch-none cursor-grab active:cursor-grabbing text-inkMuted hover:text-ink"
                aria-label="Drag indicator to reorder visual stack"
              >
                <GripVertical size={12} />
              </button>
              <span className="flex-1">{indicatorTitle(ind)}</span>
              <button
                type="button"
                data-testid={`accordion-eye-${ind.presetId}`}
                onClick={(e) => { e.stopPropagation(); onToggleVisible?.(ind.presetId) }}
                title={visible ? 'Hide' : 'Show'}
                className="text-inkMuted hover:text-ink"
                aria-label={visible ? 'Hide indicator' : 'Show indicator'}
              >
                {visible ? <Eye size={12} /> : <EyeOff size={12} />}
              </button>
              <button
                type="button"
                data-testid={`accordion-gear-${ind.presetId}`}
                onClick={(e) => { e.stopPropagation(); onOpenPopover?.(ind.presetId) }}
                title="Settings"
                className="text-inkMuted hover:text-ink"
                aria-label="Indicator settings"
              >
                <Settings size={12} />
              </button>
              <button
                type="button"
                data-testid={`accordion-trash-${ind.presetId}`}
                onClick={(e) => { e.stopPropagation(); onRemove?.(ind.presetId) }}
                title="Remove"
                className="text-inkMuted hover:text-ink"
                aria-label="Remove indicator"
              >
                <Trash2 size={12} />
              </button>
              <button
                type="button"
                data-testid={`accordion-more-${ind.presetId}`}
                onClick={(e) => {
                  e.stopPropagation()
                  setAddMenuOpen(false)
                  setActionMenuPresetId((prev) => (prev === ind.presetId ? null : ind.presetId))
                }}
                title="More actions"
                className="text-inkMuted hover:text-ink"
                aria-label="More actions"
              >
                <MoreHorizontal size={12} />
              </button>
              {actionMenuPresetId === ind.presetId && (
                <div
                  role="menu"
                  data-testid={`accordion-action-menu-${ind.presetId}`}
                  className="absolute right-0 top-6 z-40 min-w-[150px] rounded-md border border-border bg-canvas py-1 text-ink shadow-lg shadow-black/20"
                >
                  <button
                    type="button"
                    role="menuitem"
                    data-testid={`accordion-save-template-${ind.presetId}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      onSaveTemplate?.(ind.presetId)
                      setActionMenuPresetId(null)
                    }}
                    className="w-full px-3 py-1.5 text-left text-xs text-inkMuted hover:bg-canvas2 hover:text-ink"
                  >
                    Save as template
                  </button>
                </div>
              )}
            </div>
          )
        })}

        <div className="relative mt-1 border-t border-border pt-1">
          <button
            type="button"
            data-testid="accordion-add-button"
            onClick={() => setAddMenuOpen((v) => !v)}
            className="flex items-center gap-1 w-full text-left px-1 py-1 text-inkMuted hover:bg-canvas2 hover:text-ink rounded"
            aria-haspopup="menu"
            aria-expanded={addMenuOpen}
          >
            <Plus size={12} />
            <span>Add Indicator</span>
          </button>
          {addMenuOpen && (
            <div
              role="menu"
              data-testid="accordion-add-menu"
              className="absolute left-0 right-0 mt-1 max-h-[22rem] overflow-y-auto overscroll-contain rounded-md border border-border bg-canvas text-ink shadow-lg shadow-black/20 z-30"
            >
              {AVAILABLE_INDICATORS.map((opt) => {
                const disabled = isOptDisabled(opt.kind)
                const title = titleFor(opt, disabled)
                return (
                  <button
                    key={opt.kind}
                    type="button"
                    role="menuitem"
                    data-testid={`accordion-add-option-${opt.kind}`}
                    disabled={disabled}
                    title={title}
                    onClick={() => {
                      if (disabled) return
                      onAdd?.(opt.kind)
                      setAddMenuOpen(false)
                    }}
                    className={`w-full text-left px-3 py-1.5 text-xs ${
                      disabled
                        ? 'text-inkMuted/45 cursor-not-allowed'
                        : 'text-inkMuted hover:bg-canvas2 hover:text-ink'
                    }`}
                  >
                    {opt.label}
                  </button>
                )
              })}
              {indicatorTemplates.length > 0 && (
                <>
                  <div className="mx-3 my-1 border-t border-border" />
                  <div className="px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-inkMuted">
                    Saved Templates
                  </div>
                  {indicatorTemplates.map((template) => {
                    const disabled = isOptDisabled(template.kind)
                    const label = INDICATOR_DISPLAY_NAME[template.kind] || template.kind.toUpperCase()
                    return (
                      <button
                        key={template.id}
                        type="button"
                        role="menuitem"
                        data-testid={`accordion-add-template-${template.id}`}
                        disabled={disabled}
                        title={disabled ? titleFor({ kind: template.kind, label }, true) : `Add ${template.name}`}
                        onClick={() => {
                          if (disabled) return
                          onAddTemplate?.(template)
                          setAddMenuOpen(false)
                        }}
                        className={`w-full text-left px-3 py-1.5 text-xs ${
                          disabled
                            ? 'text-inkMuted/45 cursor-not-allowed'
                            : 'text-inkMuted hover:bg-canvas2 hover:text-ink'
                        }`}
                      >
                        <span className="block truncate">{template.name}</span>
                        <span className="block text-[10px] uppercase tracking-wider text-inkMuted">{label}</span>
                      </button>
                    )
                  })}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

IndicatorAccordion.propTypes = {
  indicators: PropTypes.arrayOf(
    PropTypes.shape({
      presetId: PropTypes.string.isRequired,
      kind: PropTypes.oneOf([
        'sma', 'ema', 'keltner', 'bollinger', 'donchian', 'volume',
        'vwap_1d', 'vwap_2d', 'vwap_wtd', 'vwap_mtd', 'vwap_ytd', 'vwap_earnings', 'vwap_earnings_2',
        'pivots', 'cars_v1',
      ]).isRequired,
      params: PropTypes.object.isRequired,
      style: PropTypes.object.isRequired,
    })
  ).isRequired,
  activeTimeframe: PropTypes.string,
  indicatorTemplates: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    kind: PropTypes.string.isRequired,
    params: PropTypes.object.isRequired,
    style: PropTypes.object.isRequired,
  })),
  onAdd: PropTypes.func.isRequired,
  onAddTemplate: PropTypes.func,
  onToggleVisible: PropTypes.func.isRequired,
  onOpenPopover: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  onSetAllVisible: PropTypes.func,
  onSaveTemplate: PropTypes.func,
}
